import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useManualQuery, useMutation } from 'graphql-hooks';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import ChevronLeft from '@mui/icons-material/ChevronLeft';

import { CommentsPanel } from '../../components/CommentsPanel';
import { FeedActions } from '../../components/FeedActions';
import { FeedFilterBar } from '../../components/FeedFilterBar';
import { FeedPlayer } from '../../components/FeedPlayer';
import { FeedPlayerSkeleton } from '../../components/FeedPlayerSkeleton';
import { FeedPreferenceTabMenu } from '../../components/FeedPreferenceTabMenu';
import {
  FEED_VIDEO,
  FEED_VIDEO_BY_CODESHARE,
  FEED_VIDEOS,
  FAVORITE_FEED_VIDEOS,
  GUEST_FEED_VIDEOS,
  LIKED_FEED_VIDEOS,
  PROFILE_FEED_VIDEOS,
} from '../../graphql/video/query';
import { USER_UPDATE_FEED_PREFERENCE } from '../../graphql/user/mutation';
import { Context, useAuth } from '../../context/auth';
import {
  countFeedFilters,
  EMPTY_FEED_FILTERS,
  FeedFilters,
  getFeedFiltersInput,
  countGuestVideo,
  getInstallId,
  isTyping,
  saveFeedFilters,
} from '../../helpers';
import useFpsGames from '../../hooks/useFpsGames';
import usePlayerMuted from '../../hooks/usePlayerMuted';
import useRequireAuth from '../../hooks/useRequireAuth';
import useVideoActions from '../../hooks/useVideoActions';
import { AuthReason, authModalAtom } from '../../atoms';
import { Video } from '../../types';
import { createInitialState, feedReducer } from './feedReducer';
import styles from './style';

const PAGE_LIMIT = 10;
// The next page is asked when 3 videos or less are left after the current one.
const PRELOAD_THRESHOLD = 3;
// One wheel gesture = one video. Pixels scrolled in a gesture before it moves.
const WHEEL_THRESHOLD = 30;
// After a move every wheel event is ignored this long: the slide (300ms, style.ts) plus a margin.
const WHEEL_MIN_LOCK = 350;
// No wheel event for this long: the next one starts a new gesture.
const WHEEL_GESTURE_GAP = 120;
// Trackpad momentum only decays: an event this much stronger than the previous one is a new swipe.
const WHEEL_NEW_SWIPE_RATIO = 1.4;
// A wheel in lines (Firefox) or pages, in pixels.
const WHEEL_LINE_HEIGHT = 16;
const WHEEL_PAGE_HEIGHT = 800;
// Neighbour slides: no focus, no click (the attribute is unknown to the types of React 18).
const INERT = { inert: '' };

const getWheelDelta = (event: React.WheelEvent) => {
  if (event.deltaMode === 1) {
    return event.deltaY * WHEEL_LINE_HEIGHT;
  }
  if (event.deltaMode === 2) {
    return event.deltaY * WHEEL_PAGE_HEIGHT;
  }
  return event.deltaY;
};
// A visitor is invited to sign up after this number of videos (once per visit).
// Length of a codeshare (the server refuses the others).
const CODESHARE_LENGTH = 9;

// Place of a post (player + rail) of the window: above, on screen or below.
const getSlideSx = (offset: number) => {
  if (offset < 0) {
    return styles.slidePrevious;
  }
  if (offset > 0) {
    return styles.slideNext;
  }
  return styles.slideCurrent;
};

const Feed: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth() as Context;
  const { games } = useFpsGames();
  // A visitor who is not logged in: "Pour toi" only, the actions ask to log in.
  const { isGuest, openAuthModal, requireAuth } = useRequireAuth();
  const authModal = useAtomValue(authModalAtom);
  const [feedVideos] = useManualQuery(FEED_VIDEOS, { skipCache: true });
  const [guestFeedVideos] = useManualQuery(GUEST_FEED_VIDEOS, { skipCache: true });
  const [feedVideo] = useManualQuery(FEED_VIDEO, { skipCache: true });
  const [profileVideos] = useManualQuery(PROFILE_FEED_VIDEOS, { skipCache: true });
  const [favoriteVideos] = useManualQuery(FAVORITE_FEED_VIDEOS, { skipCache: true });
  const [likedVideos] = useManualQuery(LIKED_FEED_VIDEOS, { skipCache: true });
  const [feedVideoByCodeshare] = useManualQuery(FEED_VIDEO_BY_CODESHARE, { skipCache: true });
  const [updateFeedPreference] = useMutation(USER_UPDATE_FEED_PREFERENCE);
  // The list, the position, the tab, the filters and the comments change together (a new feed
  // resets the position and closes the comments...): one reducer for all of them.
  const [state, dispatch] = useReducer(feedReducer, user.feedPreference, createInitialState);
  const {
    videos,
    index,
    loading,
    ended,
    preference,
    filters,
    commentsOpen,
    sharedId,
  } = state;
  const { muted, onForcedMute, onToggleMute } = usePlayerMuted();
  const requestIdRef = useRef(0);
  const loadingRef = useRef(false);
  const wheelSumRef = useRef(0);
  // The current gesture already moved: its momentum is ignored.
  const wheelMovedRef = useRef(false);
  const lastWheelAtRef = useRef(0);
  const lastWheelDeltaRef = useRef(0);
  const lastWheelMoveRef = useRef(0);
  // Video asked by a notification or a profile (?video=), shown first.
  const deepLinkId = searchParams.get('video');
  // Opened from a profile (?user=): its videos only, newest first, from the one clicked (like the app).
  // ?list=favorites or liked: the favorites of the connected user or the videos the profile liked.
  const profileUserId = searchParams.get('user') || '';
  const profileList = searchParams.get('list') || 'videos';
  // Comment asked by a notification (?comment=): the comments open on it.
  const deepLinkCommentId = searchParams.get('comment') || '';
  // Shared link (/short/:codeshare): the same feed, its video first.
  const { codeshare } = useParams();
  // Video of the link, kept first when a new feed comes. Read when the page arrives: the shared video can
  // come after the first page was asked.
  const linkedIdRef = useRef('');
  linkedIdRef.current = deepLinkId || sharedId;
  // Video on screen: a new feed keeps it first when it matches, it goes on playing without a reload.
  const currentIdRef = useRef('');
  const knownIdsRef = useRef([] as string[]);
  const watchedIdsRef = useRef([] as string[]);

  // The actions of the column change the list like a setState (same signature as before).
  const setVideos = useCallback((update: React.SetStateAction<Video[]>) => {
    dispatch({ type: 'videosUpdated', update });
  }, []);

  // The videos of a profile, page after page; the first page starts on the video clicked.
  const getProfilePage = useCallback(async (reset: boolean, requestId: number) => {
    const offset = reset ? 0 : knownIdsRef.current.length;
    let docs = [] as Video[];
    try {
      const page = { offset, limit: PAGE_LIMIT, disableTotalDocs: true };
      let request;
      if (profileList === 'favorites') {
        // Private list: the server takes the connected user.
        request = favoriteVideos({ variables: { input: page } });
      } else if (profileList === 'liked') {
        request = likedVideos({ variables: { input: { ...page, userId: profileUserId } } });
      } else {
        request = profileVideos({ variables: { input: { ...page, userId: profileUserId } } });
      }
      const { data } = await request as any;
      const result = data && (data.videosV2Pagination || data.favoriteVideosPagination || data.likedVideosPagination);
      docs = (result && result.docs) || [];
    } catch (e) {
      docs = [];
    }
    if (requestId !== requestIdRef.current) {
      return;
    }
    const isLastPage = docs.length < PAGE_LIMIT;
    if (reset) {
      let first = docs;
      let position = docs.findIndex((v) => v.id === deepLinkId);
      // An older video, not in the first page: it goes first.
      if (deepLinkId && position < 0) {
        const { data } = await feedVideo({ variables: { id: deepLinkId } }) as any;
        if (requestId !== requestIdRef.current) {
          return;
        }
        if (data && data.video) {
          first = [data.video, ...docs];
        }
        position = 0;
      }
      loadingRef.current = false;
      dispatch({
        type: 'profileVideosReplaced',
        videos: first,
        index: Math.max(position, 0),
        ended: isLastPage,
      });
      return;
    }
    loadingRef.current = false;
    dispatch({ type: 'videosAppended', docs, ended: isLastPage });
  }, [deepLinkId, favoriteVideos, feedVideo, likedVideos, profileList, profileUserId, profileVideos]);

  const getPage = useCallback(async (reset: boolean) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    loadingRef.current = true;
    dispatch({ type: 'pageRequested' });
    if (profileUserId) {
      await getProfilePage(reset, requestId);
      return;
    }
    const input = {
      limit: PAGE_LIMIT,
      disableTotalDocs: true,
      random: true,
      home: true,
      // A new session of the feed: the server forgets the videos already served.
      resetHome: reset,
      follow: preference === 'following',
      weeklyCheck: preference === 'ranking',
      ...getFeedFiltersInput(filters),
    };
    let data;
    try {
      if (isGuest) {
        const installId = getInstallId();
        ({ data } = await guestFeedVideos({ variables: { input: { installId, limit: PAGE_LIMIT } } }) as any);
      } else {
        ({ data } = await feedVideos({ variables: { input } }) as any);
      }
    } catch (e) {
      data = null;
    }
    if (requestId !== requestIdRef.current) {
      return;
    }
    const page = data && (data.videosV2Pagination || data.guestVideosPagination);
    const docs = ((page && page.docs) || []) as Video[];
    // No new video in the page: the end of the feed.
    const isEnded = !docs.length || (!reset && docs.every((d) => knownIdsRef.current.includes(d.id)));
    loadingRef.current = false;
    if (!reset) {
      dispatch({ type: 'videosAppended', docs, ended: isEnded });
      return;
    }
    const linkedId = linkedIdRef.current;
    dispatch({
      type: 'feedReplaced',
      docs,
      keepId: linkedId || currentIdRef.current,
      linkedId,
      ended: isEnded,
    });
  }, [feedVideos, filters, getProfilePage, guestFeedVideos, isGuest, preference, profileUserId]);

  // A new feed each time the tab or the filters change, and after the login of a visitor.
  // The current video stays on screen until the new one comes (no black screen, no reload).
  useEffect(() => {
    dispatch({ type: 'feedRestarted' });
    getPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preference, filters, isGuest, profileUserId, profileList]);

  // The video of the link goes first, the feed continues after it (the videos of a profile place it themselves).
  useEffect(() => {
    if (!deepLinkId || profileUserId) {
      return;
    }
    let active = true;
    const getDeepLinkVideo = async () => {
      const { data } = await feedVideo({ variables: { id: deepLinkId } }) as any;
      if (active && data && data.video) {
        dispatch({ type: 'deepLinkVideoPinned', video: data.video, openComments: deepLinkCommentId !== '' });
      }
    };
    getDeepLinkVideo();
    return () => {
      active = false;
    };
  }, [deepLinkCommentId, deepLinkId, feedVideo, profileUserId]);

  // The video of a shared link goes first too. A link of a removed video: the feed alone.
  useEffect(() => {
    if (!codeshare || codeshare.length !== CODESHARE_LENGTH) {
      return undefined;
    }
    let active = true;
    const getSharedVideo = async () => {
      const { data } = await feedVideoByCodeshare({ variables: { codeshare } }) as any;
      const video = data && data.videoByCodeshare as Video | undefined;
      if (active && video) {
        dispatch({ type: 'sharedVideoPinned', video });
      }
    };
    getSharedVideo();
    return () => {
      active = false;
    };
  }, [codeshare, feedVideoByCodeshare]);

  const current = videos[index] as Video | undefined;
  currentIdRef.current = current ? current.id : '';
  knownIdsRef.current = videos.map((v) => v.id);
  // The previous and next players stay mounted and preloaded: a move slides the stack, the next one is ready.
  const windowStart = Math.max(index - 1, 0);
  const windowVideos = useMemo(() => videos.slice(windowStart, index + 2), [index, videos, windowStart]);

  // Next page before the end of the list.
  useEffect(() => {
    if (!ended && !loadingRef.current && videos.length && videos.length - index - 1 <= PRELOAD_THRESHOLD) {
      getPage(false);
    }
  }, [ended, getPage, index, videos.length]);

  const currentId = current ? current.id : '';
  const {
    onCommented,
    onFavorite,
    onFollow,
    onLike,
    onShare,
    shareLabel,
  } = useVideoActions({ current, setVideos, isGuest });

  // Page of the current video, opened again after the login of a visitor.
  const guestOptions = useMemo(() => ({
    redirect: currentId ? `/feed?video=${currentId}` : '/feed',
    username: current && current.user ? current.user.username : '',
  }), [current, currentId]);

  const onLocked = useCallback((reason: AuthReason) => {
    openAuthModal(reason, guestOptions);
  }, [guestOptions, openAuthModal]);

  const lockTabs = useCallback(() => {
    onLocked('feedTabs');
  }, [onLocked]);

  // Every few videos the invitation to sign up opens, unless the login popup is already up.
  useEffect(() => {
    if (!isGuest || !currentId || watchedIdsRef.current.includes(currentId)) {
      return;
    }
    watchedIdsRef.current = [...watchedIdsRef.current, currentId];
    if (countGuestVideo() && !authModal) {
      onLocked('signupInvite');
    }
  }, [currentId, isGuest, onLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // The comments belong to a video: they close when another one comes.
  const goTo = useCallback((delta: number) => {
    dispatch({ type: 'moved', delta });
  }, []);

  const goPrevious = useCallback(() => {
    goTo(-1);
  }, [goTo]);

  const goNext = useCallback(() => {
    goTo(1);
  }, [goTo]);

  // ↑ / ↓ change the video, except in an input.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target)) {
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        goTo(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        goTo(-1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goTo]);

  // One move per gesture (a swipe with its momentum, or a mouse notch). After a short lock, a new gesture
  // is a pause in the events, a change of direction or an event stronger than the decaying momentum.
  const onWheel = useCallback((event: React.WheelEvent) => {
    const now = Date.now();
    const delta = getWheelDelta(event);
    const previous = lastWheelDeltaRef.current;
    const isPause = now - lastWheelAtRef.current >= WHEEL_GESTURE_GAP;
    lastWheelAtRef.current = now;
    lastWheelDeltaRef.current = delta;
    if (!delta || now - lastWheelMoveRef.current < WHEEL_MIN_LOCK) {
      return;
    }
    const isReversed = previous !== 0 && Math.sign(delta) !== Math.sign(previous);
    const isStronger = Math.abs(delta) > WHEEL_THRESHOLD && Math.abs(delta) > Math.abs(previous) * WHEEL_NEW_SWIPE_RATIO;
    if (isPause || isReversed || isStronger) {
      wheelSumRef.current = 0;
      wheelMovedRef.current = false;
    }
    if (wheelMovedRef.current) {
      return;
    }
    wheelSumRef.current += delta;
    if (Math.abs(wheelSumRef.current) < WHEEL_THRESHOLD) {
      return;
    }
    const direction = wheelSumRef.current > 0 ? 1 : -1;
    wheelMovedRef.current = true;
    wheelSumRef.current = 0;
    lastWheelMoveRef.current = now;
    goTo(direction);
  }, [goTo]);

  const toggleComments = useCallback(() => {
    dispatch({ type: 'commentsToggled' });
  }, []);

  const closeComments = useCallback(() => {
    dispatch({ type: 'commentsClosed' });
  }, []);

  // A click on the empty part of the stage (not the player, its rail, the arrows or the panel)
  // closes the comments, as one expects from a panel.
  const onStageClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (commentsOpen && target.dataset && target.dataset.feedEmpty) {
      closeComments();
    }
  }, [closeComments, commentsOpen]);

  const onOpenUser = useCallback(() => {
    if (current) {
      navigate(`/u/${current.userId}`);
    }
  }, [current, navigate]);

  // Reserved to the connected users: a visitor gets the login popup with the reason.
  const guardLike = useCallback(() => {
    requireAuth('like', onLike, guestOptions);
  }, [guestOptions, onLike, requireAuth]);

  const guardFavorite = useCallback(() => {
    requireAuth('favorite', onFavorite, guestOptions);
  }, [guestOptions, onFavorite, requireAuth]);

  const guardFollow = useCallback(() => {
    requireAuth('follow', onFollow, guestOptions);
  }, [guestOptions, onFollow, requireAuth]);

  const guardOpenUser = useCallback(() => {
    requireAuth('profile', onOpenUser, guestOptions);
  }, [guestOptions, onOpenUser, requireAuth]);

  // Comments already open (link of a notification): the button still closes them.
  const guardToggleComments = useCallback(() => {
    if (commentsOpen) {
      toggleComments();
      return;
    }
    requireAuth('comment', toggleComments, guestOptions);
  }, [commentsOpen, guestOptions, requireAuth, toggleComments]);

  // Another tab or other filters: the video of the link is not kept, the address goes back to /feed.
  const leaveLink = useCallback(() => {
    if (deepLinkId) {
      setSearchParams({}, { replace: true });
    }
    if (codeshare) {
      dispatch({ type: 'sharedLinkLeft' });
      navigate('/feed', { replace: true });
    }
  }, [codeshare, deepLinkId, navigate, setSearchParams]);

  // Same preference as the app: saved on the user.
  const onChangePreference = useCallback((value: string) => {
    leaveLink();
    dispatch({ type: 'preferenceChanged', preference: value });
    updateFeedPreference({ variables: { input: { feedPreference: value } } });
  }, [leaveLink, updateFeedPreference]);

  const applyFilters = useCallback((value: FeedFilters) => {
    leaveLink();
    saveFeedFilters(value);
    dispatch({ type: 'filtersChanged', filters: value });
  }, [leaveLink]);

  const clearFilters = useCallback(() => {
    applyFilters(EMPTY_FEED_FILTERS);
  }, [applyFilters]);

  const filtersCount = countFeedFilters(filters);

  const stageSx = useMemo(() => {
    if (commentsOpen) {
      return { ...styles.stage, ...styles.stageWithComments };
    }
    return styles.stage;
  }, [commentsOpen]);

  const isEmpty = !loading && !current;

  // Back to the profile the videos were opened from.
  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/feed');
    }
  }, [navigate]);
  const profileTitle = useMemo(() => {
    if (profileList === 'favorites') {
      return t('admin.feed.listFavorites');
    }
    if (profileList === 'liked') {
      return t('admin.feed.listLiked');
    }
    return t('admin.feed.profileVideos', { username: current && current.user ? current.user.username : '' });
  }, [current, profileList, t]);

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        {profileUserId ? (
          <Box sx={styles.profileHeader}>
            <IconButton onClick={goBack} aria-label={t('admin.common.back')} sx={styles.back}>
              <ChevronLeft />
            </IconButton>
            <Typography component="h1" sx={styles.profileTitle}>
              {profileTitle}
            </Typography>
          </Box>
        ) : (
          <>
            <FeedPreferenceTabMenu value={preference} onChange={onChangePreference} onLocked={isGuest ? lockTabs : undefined} />
            <FeedFilterBar filters={filters} games={games} onChange={applyFilters} onLocked={isGuest ? onLocked : undefined} />
          </>
        )}
      </Box>

      <Box sx={stageSx} onWheel={onWheel} onClick={onStageClick} data-feed-empty="true">
        {current && (
          <>
            {/* Each post slides as a whole, its rail included. The actions target the current video:
                the neighbours are inert. */}
            <Box sx={styles.viewport} data-feed-empty="true">
              {windowVideos.map((video, position) => {
                const offset = windowStart + position - index;
                const isCurrent = offset === 0;
                return (
                  <Box key={video.id} sx={[getSlideSx(offset), commentsOpen && styles.slideWithComments]} aria-hidden={!isCurrent} data-feed-empty="true" {...(isCurrent ? {} : INERT)}>
                    <FeedPlayer
                      video={video}
                      active={isCurrent}
                      muted={muted}
                      onToggleMute={onToggleMute}
                      onForcedMute={onForcedMute}
                      onOpenUser={guardOpenUser}
                    />
                    <FeedActions
                      video={video}
                      isOwn={video.userId === user.id}
                      commentsOpen={isCurrent && commentsOpen}
                      onOpenUser={guardOpenUser}
                      onFollow={guardFollow}
                      shareLabel={isCurrent ? shareLabel : ''}
                      onLike={guardLike}
                      onToggleComments={guardToggleComments}
                      onFavorite={guardFavorite}
                      onShare={onShare}
                    />
                  </Box>
                );
              })}
            </Box>
            {commentsOpen && (
              <CommentsPanel
                video={current}
                highlightId={current.id === deepLinkId ? deepLinkCommentId : undefined}
                onClose={closeComments}
                onCommented={onCommented}
                onLocked={isGuest ? onLocked : undefined}
              />
            )}
          </>
        )}
        {loading && !current && <FeedPlayerSkeleton />}
        {isEmpty && (
          <Box sx={styles.empty}>
            <Typography sx={styles.emptyText}>
              {filtersCount ? t('admin.feed.emptyFiltered') : t('admin.feed.empty')}
            </Typography>
            {filtersCount > 0 && <Button onClick={clearFilters} sx={styles.emptyButton}>{t('admin.feed.clearFilters')}</Button>}
          </Box>
        )}
      </Box>

      <Box sx={styles.arrows}>
        <IconButton disableRipple onClick={goPrevious} disabled={index === 0} aria-label={t('admin.feed.previous')} sx={styles.arrow}>
          <KeyboardArrowUp />
        </IconButton>
        <IconButton disableRipple onClick={goNext} disabled={index >= videos.length - 1} aria-label={t('admin.feed.next')} sx={styles.arrow}>
          <KeyboardArrowDown />
        </IconButton>
      </Box>
    </Box>
  );
});

export default Feed;
